import { DateInput } from '../../../../components/DateInput/DateInput';
import React, { useState, useEffect } from 'react';
import { profileService, UserPersonalInformationDto } from '../../../../services/profile.service';
import { toast } from 'react-toastify';
import { validatePersonalInformation } from '../../../../utils/investorValidation';
import { normalizeDateForInput, normalizeCountryOfResidenceForForm } from '../../../../utils/formHelpers';
import { SharedFormContext } from '../shared/types';
import { useLiveErrorClearing } from '../shared/useLiveErrorClearing';
import { useDelegationPermissions } from '../../../../contexts/DelegationPermissionsContext';
import { agentReadOnlyNote } from '../../../../utils/delegationScope';
import { getPermissionErrorMessage } from '../../../../utils/apiClient';
import { PremiumSelect } from '../../../../components/PremiumSelect/PremiumSelect';
import { isResidentIndividual } from '../../../../config/profileVisibility';

// Dropdown values are UPPERCASE — see docs/convention in shared/constants.ts.
// Labels stay in readable case for display. Marital status values stay
// numeric because they're position codes, not language tokens.
// Must cover every master_title value, since registration writes the investor's choice
// through as a token (2026-09-07): Mr./Mrs./Ms./Dr./Capt./Miss. A value with no option
// here renders as an empty MANDATORY select.
const TITLE_OPTIONS = [
  { value: 'MR', label: 'Mr' },
  { value: 'MRS', label: 'Mrs' },
  { value: 'MISS', label: 'Miss' },
  { value: 'MS', label: 'Ms' },
  { value: 'DR', label: 'Dr' },
  { value: 'CAPT', label: 'Capt' },
];

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'TRANSGENDER', label: 'Transgender' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: '1', label: 'Single' },
  { value: '2', label: 'Married' },
  { value: '3', label: 'Widowed' },
  { value: '5', label: 'Seperated' },
  { value: '4', label: 'Divorced' },
];

const MAIDEN_TITLE_OPTIONS = [
  { value: 'MR', label: 'Mr' },
  { value: 'MRS', label: 'Mrs' },
  { value: 'MISS', label: 'Miss' },
];

const FATHER_TITLE_OPTIONS = [
  { value: 'MR', label: 'Mr' },
  { value: 'SHRI', label: 'Shri' },
  { value: 'LATE', label: 'Late' },
];

const MOTHER_TITLE_OPTIONS = [
  { value: 'MRS', label: 'Mrs' },
  { value: 'MISS', label: 'Miss' },
  { value: 'SHRIMATI', label: 'Shrimati' },
  { value: 'LATE', label: 'Late' },
];

const SPOUSE_TITLE_OPTIONS = [
  { value: 'MR', label: 'Mr' },
  { value: 'MRS', label: 'Mrs' },
  { value: 'MISS', label: 'Miss' },
];

/** Matches Laravel's readonly/disabled inputs, which render greyed out. */
const LOCKED_FIELD_STYLE: React.CSSProperties = { backgroundColor: '#e9ecef', color: '#495057', cursor: 'not-allowed' };

interface PersonalInformationFormProps {
  initialData: UserPersonalInformationDto | null;
  onSave: () => void;
  canEdit: boolean;
  canSubmit: boolean;
  isProxyMode: boolean;
  sharedContext: SharedFormContext;
}

export const PersonalInformationForm: React.FC<PersonalInformationFormProps> = ({
  initialData,
  onSave,
  canEdit,
  isProxyMode,
  sharedContext
}) => {
  const delegationPerms = useDelegationPermissions();
  const [formData, setFormData] = useState<Partial<UserPersonalInformationDto>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Per-type profile rules. RI tightens the maiden-name visibility to
  // Female + Married (vs the default Female OR Married used elsewhere) and
  // pre-fills Citizenship / Country of Residence to India so the hidden-for-
  // RI inputs don't submit blanks.
  const investorType = sharedContext.dashboardData?.investor?.investorType ?? undefined;
  const isRi = isResidentIndividual(investorType);

  // Laravel renders Country Of Birth as a select over master_countries
  // (uppercased labels). Same list the container already loads.
  const COUNTRY_OF_BIRTH_OPTIONS = React.useMemo(
    () =>
      sharedContext.taxResidencyCountries
        .filter((c) => c.ssName)
        .map((c) => ({ value: c.ssName!.toUpperCase(), label: c.ssName!.toUpperCase() })),
    [sharedContext.taxResidencyCountries]
  );

  // Gender is locked only when it actually arrived from the introduction. Locking it
  // unconditionally left a MANDATORY field empty and unfillable whenever the provider had
  // not supplied one, which blocked the whole section. Decided once on hydration so the
  // investor's own selection does not lock the control under them mid-edit.
  const [genderLocked, setGenderLocked] = useState(false);

  // Same rule generalised to ALL registration-locked identity fields (user 2026-08-30):
  // a journey introduced for a DIFFERENT person on the same email arrives with only the
  // intro's name parts — an unconditionally-locked-but-empty mandatory field (DOB here)
  // made the section impossible to complete. Locked+grey iff the field actually carried
  // a value at hydration; empty fields stay editable. OCR-filled values re-lock via this
  // same rule on the post-save reload (the fill lands them non-empty).
  const [prefillLocked, setPrefillLocked] = useState<Record<string, boolean>>({});
  const lockIf = (key: string) => !!prefillLocked[key];
  const lockStyle = (key: string): React.CSSProperties | undefined =>
    prefillLocked[key] ? LOCKED_FIELD_STYLE : undefined;

  useEffect(() => {
    if (initialData) {
      const hydrated: Partial<UserPersonalInformationDto> = {
        ...initialData,
        userDob: normalizeDateForInput(initialData.userDob),
        countryOfResidence: normalizeCountryOfResidenceForForm(initialData.countryOfResidence),
        // Country Of Birth is a select now — match the uppercased option values.
        countryDob: initialData.countryDob ? initialData.countryDob.toUpperCase() : initialData.countryDob,
        // Gender options are UPPERCASE ('MALE'…) but self-registration stored Title-case
        // ('Male') — an exact-match select then showed EMPTY while genderLocked locked it
        // (locked AND blank, seen live 2026-09-05 investor 202609055463). Normalise on read.
        gender: initialData.gender ? initialData.gender.toUpperCase() : initialData.gender,
      };
      if (isRi) {
        if (!hydrated.citizenship) hydrated.citizenship = 'India';
        if (!hydrated.countryOfResidence) hydrated.countryOfResidence = 'India';
      }
      // Citizenship and Country of Residence are read-only here (Laravel posts
      // them from a hidden input). Fall back to the registration record so a
      // required field can never render blank with no way to fill it.
      const dash = sharedContext.dashboardData?.investor;
      if (!hydrated.citizenship && dash?.nationality) hydrated.citizenship = dash.nationality;
      if (!hydrated.countryOfResidence && dash?.countryOfResidence) {
        hydrated.countryOfResidence = dash.countryOfResidence;
      }
      setGenderLocked(!!(hydrated.gender && String(hydrated.gender).trim()));
      const filled = (v: unknown) => !!(v && String(v).trim());
      setPrefillLocked({
        investorFirstName: filled(hydrated.investorFirstName),
        investorMiddleName: filled(hydrated.investorMiddleName),
        investorLastName: filled(hydrated.investorLastName),
        userDob: filled(hydrated.userDob),
        citizenship: filled(hydrated.citizenship),
        countryOfResidence: filled(hydrated.countryOfResidence),
      });
      setFormData(hydrated);
    }
  }, [initialData, isRi, sharedContext.dashboardData]);

  /**
   * QA #10 — Country of Birth defaults from the locked Citizenship.
   *
   * nationality.ss_nationality and country.ss_country are both ISO 3166 codes, so the two
   * masters join in one hop (Indian -> IN -> India; 238 of 248 rows resolve).
   *
   * Its OWN effect, keyed on the lookup lists: the container fetches them asynchronously, so
   * doing this during hydration silently did nothing whenever the lists landed a moment later.
   * A DEFAULT only — an Indian citizen may be born abroad, so the field stays editable, and a
   * value already on file is never touched.
   */
  useEffect(() => {
    const citizenship = String(formData.citizenship ?? '').trim();
    if (!citizenship || String(formData.countryDob ?? '').trim()) return;
    const nats = sharedContext.nationalities;
    if (!nats?.length || !sharedContext.taxResidencyCountries.length) return;
    const iso = nats.find((n) => (n.name || '').trim().toLowerCase() === citizenship.toLowerCase())?.externalId;
    if (!iso) return;
    const match = sharedContext.taxResidencyCountries
      .find((c) => (c.ssCountry || '').trim().toUpperCase() === iso.trim().toUpperCase());
    if (match?.ssName) {
      setFormData((prev) => (String((prev as any).countryDob ?? '').trim()
        ? prev
        : { ...prev, countryDob: match.ssName!.toUpperCase() }));
    }
  }, [formData.citizenship, formData.countryDob, sharedContext.nationalities, sharedContext.taxResidencyCountries]);

  // Corrected fields drop their error immediately (remove-only live re-check).
  useLiveErrorClearing(errors, setErrors, () => validatePersonalInformation(formData as UserPersonalInformationDto), [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validatePersonalInformation(formData as UserPersonalInformationDto);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError ? `Please fix: ${firstError}` : 'Please fix the errors in the form');
      return;
    }

    setErrors({});
    setSaving(true);

    // Address is owned by the Contact Details tab. Drop the hydrated copy so a
    // save here cannot write back an address the investor has since changed there.
    const {
      addressLine1, addressLine2, addressLine3,
      userCity, userState, userZipCode, userCountry,
      ...payload
    } = formData;

    try {
      await profileService.updatePersonalInfo(payload as UserPersonalInformationDto);
      toast.success('Personal information updated');
      onSave();
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      if (permissionError) {
        toast.error(permissionError);
      } else {
        toast.error(err.response?.data?.message || 'Failed to update');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="investor-profile__card" onSubmit={handleSubmit}>
      {/* Personal Information */}
      <div className="form-group form-group--full">
        <h3 style={{ marginBottom: '10px' }}>Personal Information</h3>
      </div>
      <div className="investor-profile__grid">
        <div className="form-group">
          <label>Title <span className="text-danger">*</span></label>
          <PremiumSelect
            value={formData.nameTitle ?? ''}
            onChange={(val) => setFormData({ ...formData, nameTitle: val })}
            options={TITLE_OPTIONS}
            error={errors.nameTitle}
          />
        </div>
        {/* Laravel locks the identity fields captured at registration — but only when a
            value was actually captured (see prefillLocked): a different-person intro on
            the same email supplies just the name, and a locked empty mandatory field
            (DOB) blocked the section. Empty-at-hydration fields stay editable. */}
        <div className="form-group">
          <label>First Name <span className="text-danger">*</span></label>
          <input
            value={formData.investorFirstName ?? ''}
            readOnly={lockIf('investorFirstName')}
            onChange={(e) => setFormData({ ...formData, investorFirstName: e.target.value })}
            className={errors.investorFirstName ? 'form-control is-invalid' : 'form-control'}
            style={lockStyle('investorFirstName')}
          />
          {errors.investorFirstName && <div className="invalid-feedback">{errors.investorFirstName}</div>}
        </div>

        <div className="form-group">
          <label>Middle Name</label>
          <input
            value={formData.investorMiddleName ?? ''}
            readOnly={lockIf('investorMiddleName')}
            onChange={(e) => setFormData({ ...formData, investorMiddleName: e.target.value })}
            className="form-control"
            style={lockStyle('investorMiddleName')}
          />
        </div>

        <div className="form-group">
          <label>Last Name <span className="text-danger">*</span></label>
          <input
            value={formData.investorLastName ?? ''}
            readOnly={lockIf('investorLastName')}
            onChange={(e) => setFormData({ ...formData, investorLastName: e.target.value })}
            className={errors.investorLastName ? 'form-control is-invalid' : 'form-control'}
            style={lockStyle('investorLastName')}
          />
          {errors.investorLastName && <div className="invalid-feedback">{errors.investorLastName}</div>}
        </div>

        <div className="form-group">
          <label>Date Of Birth <span className="text-danger">*</span></label>
          <DateInput
            value={formData.userDob ?? ''}
            readOnly={lockIf('userDob')}
            onChange={(iso) => setFormData({ ...formData, userDob: iso })}
            className={errors.userDob ? 'form-control is-invalid' : 'form-control'}
            style={lockStyle('userDob')}
          />
          {errors.userDob && <div className="invalid-feedback d-block">{errors.userDob}</div>}
        </div>

        <div className="form-group">
          <label>Gender <span className="text-danger">*</span></label>
          <PremiumSelect
            value={formData.gender ?? ''}
            onChange={(val) => setFormData({ ...formData, gender: val })}
            options={GENDER_OPTIONS}
            disabled={genderLocked}
            error={errors.gender}
          />
        </div>

        <div className="form-group">
          <label>Marital Status <span className="text-danger">*</span></label>
          <PremiumSelect
            value={formData.maritalStatus ?? ''}
            onChange={(val) => setFormData({ ...formData, maritalStatus: val })}
            options={MARITAL_STATUS_OPTIONS}
            error={errors.maritalStatus}
          />
        </div>

        {/* Maiden Name — Laravel (blade toggleMaidenName): Female AND not
            Single. RI keeps its tighter Female AND Married rule. */}
        {(isRi
          ? formData.gender === 'FEMALE' && formData.maritalStatus === '2'
          : formData.gender === 'FEMALE' && formData.maritalStatus !== '1') && (
          <>
            {/* Maiden Name (if applicable) */}
            <div className="form-group form-group--full">
              <h3 style={{ marginTop: '1.5rem' }}>Maiden Name (if applicable)</h3>
            </div>
            <div className="form-group">
              <label>Title <span className="text-danger">*</span></label>
              <PremiumSelect
                value={formData.maidenTitle ?? ''}
                onChange={(val) => setFormData({ ...formData, maidenTitle: val })}
                options={MAIDEN_TITLE_OPTIONS}
              />
            </div>
            <div className="form-group">
              <label>Maiden First Name <span className="text-danger">*</span></label>
              <input
                className={errors.maidenName ? 'form-control is-invalid' : 'form-control'}
                value={formData.maidenName ?? ''}
                onChange={(e) => setFormData({ ...formData, maidenName: e.target.value })}
              />
              {errors.maidenName && <div className="invalid-feedback">{errors.maidenName}</div>}
            </div>
            <div className="form-group">
              <label>Maiden Middle Name</label>
              <input
                className="form-control"
                value={formData.maidenMiddleName ?? ''}
                onChange={(e) => setFormData({ ...formData, maidenMiddleName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Maiden Last Name <span className="text-danger">*</span></label>
              <input
                className={errors.maidenLastName ? 'form-control is-invalid' : 'form-control'}
                value={formData.maidenLastName ?? ''}
                onChange={(e) => setFormData({ ...formData, maidenLastName: e.target.value })}
              />
              {errors.maidenLastName && <div className="invalid-feedback">{errors.maidenLastName}</div>}
            </div>
          </>
        )}

        <div className="form-group">
          <label>City Of Birth <span className="text-danger">*</span></label>
          <input
            value={formData.cityOfDob ?? ''}
            onChange={(e) => setFormData({ ...formData, cityOfDob: e.target.value })}
            className={errors.cityOfDob ? 'form-control is-invalid' : 'form-control'}
          />
          {errors.cityOfDob && <div className="invalid-feedback">{errors.cityOfDob}</div>}
        </div>

        <div className="form-group">
          <label>Country Of Birth <span className="text-danger">*</span></label>
          <PremiumSelect
            value={formData.countryDob ?? ''}
            onChange={(val) => setFormData({ ...formData, countryDob: val })}
            options={COUNTRY_OF_BIRTH_OPTIONS}
            placeholder="Select Country"
            error={errors.countryDob}
            searchable
          />
        </div>

        <div className="form-group">
          <label>Citizenship <span className="text-danger">*</span></label>
          <input
            value={formData.citizenship ?? ''}
            readOnly={lockIf('citizenship')}
            onChange={(e) => setFormData({ ...formData, citizenship: e.target.value })}
            className={errors.citizenship ? 'form-control is-invalid' : 'form-control'}
            style={lockStyle('citizenship')}
          />
          {errors.citizenship && <div className="invalid-feedback">{errors.citizenship}</div>}
        </div>

        <div className="form-group">
          <label>Country of Residence <span className="text-danger">*</span></label>
          <input
            value={formData.countryOfResidence ?? ''}
            readOnly={lockIf('countryOfResidence')}
            onChange={(e) => setFormData({ ...formData, countryOfResidence: e.target.value })}
            className={errors.countryOfResidence ? 'form-control is-invalid' : 'form-control'}
            style={lockStyle('countryOfResidence')}
          />
          {errors.countryOfResidence && <div className="invalid-feedback">{errors.countryOfResidence}</div>}
        </div>

        <div className="form-group">
          <label>PAN Number <span className="text-danger">*</span></label>
          <input
            className={errors.panNumber ? 'form-control is-invalid' : 'form-control'}
            value={formData.panNumber ?? ''}
            onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
          />
          {errors.panNumber && <div className="invalid-feedback">{errors.panNumber}</div>}
        </div>

        {/* Father's Details */}
        <div className="form-group form-group--full">
          <h3 style={{ marginTop: '1.5rem' }}>Father's Details</h3>
        </div>
        <div className="form-group">
          <label>Title</label>
          <PremiumSelect
            value={formData.fatherNameTitle ?? ''}
            onChange={(val) => setFormData({ ...formData, fatherNameTitle: val })}
            options={FATHER_TITLE_OPTIONS}
          />
        </div>
        <div className="form-group">
          <label>Father's First Name <span className="text-danger">*</span></label>
          <input
            value={formData.fathersFirstName ?? ''}
            onChange={(e) => setFormData({ ...formData, fathersFirstName: e.target.value })}
            disabled={sharedContext.isOcrLocked('user_personal_information.fathers_first_name', formData.fathersFirstName)}
            title={sharedContext.isOcrLocked('user_personal_information.fathers_first_name', formData.fathersFirstName) ? 'Verified from your document — change it in the Documents Center' : undefined}
            className={errors.fathersFirstName ? 'form-control is-invalid' : 'form-control'}
          />
          {errors.fathersFirstName && <div className="invalid-feedback">{errors.fathersFirstName}</div>}
        </div>
        <div className="form-group">
          <label>Father's Middle Name</label>
          <input
            className="form-control"
            value={formData.fathersMiddleName ?? ''}
            onChange={(e) => setFormData({ ...formData, fathersMiddleName: e.target.value })}
            disabled={sharedContext.isOcrLocked('user_personal_information.fathers_middle_name', formData.fathersMiddleName)}
            title={sharedContext.isOcrLocked('user_personal_information.fathers_middle_name', formData.fathersMiddleName) ? 'Verified from your document — change it in the Documents Center' : undefined}
          />
        </div>
        <div className="form-group">
          <label>Father's Last Name <span className="text-danger">*</span></label>
          <input
            value={formData.fathersLastName ?? ''}
            onChange={(e) => setFormData({ ...formData, fathersLastName: e.target.value })}
            disabled={sharedContext.isOcrLocked('user_personal_information.fathers_last_name', formData.fathersLastName)}
            title={sharedContext.isOcrLocked('user_personal_information.fathers_last_name', formData.fathersLastName) ? 'Verified from your document — change it in the Documents Center' : undefined}
            className={errors.fathersLastName ? 'form-control is-invalid' : 'form-control'}
          />
          {errors.fathersLastName && <div className="invalid-feedback">{errors.fathersLastName}</div>}
        </div>

        {/* Mother's Details */}
        <div className="form-group form-group--full">
          <h3 style={{ marginTop: '1.5rem' }}>Mother's Details</h3>
        </div>
        <div className="form-group">
          <label>Title</label>
          <PremiumSelect
            value={formData.motherNameTitle ?? ''}
            onChange={(val) => setFormData({ ...formData, motherNameTitle: val })}
            options={MOTHER_TITLE_OPTIONS}
          />
        </div>
        <div className="form-group">
          <label>Mother's First Name <span className="text-danger">*</span></label>
          <input
            value={formData.motherFirstName ?? ''}
            onChange={(e) => setFormData({ ...formData, motherFirstName: e.target.value })}
            disabled={sharedContext.isOcrLocked('user_personal_information.mother_first_name', formData.motherFirstName)}
            title={sharedContext.isOcrLocked('user_personal_information.mother_first_name', formData.motherFirstName) ? 'Verified from your document — change it in the Documents Center' : undefined}
            className={errors.motherFirstName ? 'form-control is-invalid' : 'form-control'}
          />
          {errors.motherFirstName && <div className="invalid-feedback">{errors.motherFirstName}</div>}
        </div>
        <div className="form-group">
          <label>Mother's Middle Name</label>
          <input
            className="form-control"
            value={formData.motherMiddleName ?? ''}
            onChange={(e) => setFormData({ ...formData, motherMiddleName: e.target.value })}
            disabled={sharedContext.isOcrLocked('user_personal_information.mother_middle_name', formData.motherMiddleName)}
            title={sharedContext.isOcrLocked('user_personal_information.mother_middle_name', formData.motherMiddleName) ? 'Verified from your document — change it in the Documents Center' : undefined}
          />
        </div>
        <div className="form-group">
          <label>Mother's Last Name <span className="text-danger">*</span></label>
          <input
            value={formData.motherLastName ?? ''}
            onChange={(e) => setFormData({ ...formData, motherLastName: e.target.value })}
            disabled={sharedContext.isOcrLocked('user_personal_information.mother_last_name', formData.motherLastName)}
            title={sharedContext.isOcrLocked('user_personal_information.mother_last_name', formData.motherLastName) ? 'Verified from your document — change it in the Documents Center' : undefined}
            className={errors.motherLastName ? 'form-control is-invalid' : 'form-control'}
          />
          {errors.motherLastName && <div className="invalid-feedback">{errors.motherLastName}</div>}
        </div>

        {/* Spouse Details — Laravel (blade toggleSpouseFields): Male AND Married. */}
        {formData.gender === 'MALE' && formData.maritalStatus === '2' && (
          <>
            {/* Spouse Details */}
            <div className="form-group form-group--full">
              <h3 style={{ marginTop: '1.5rem' }}>Spouse Details</h3>
            </div>
            <div className="form-group">
              <label>Title</label>
              <PremiumSelect
                value={formData.spouseNameTitle ?? ''}
                onChange={(val) => setFormData({ ...formData, spouseNameTitle: val })}
                options={SPOUSE_TITLE_OPTIONS}
              />
            </div>
            <div className="form-group">
              <label>Spouse First Name</label>
              <input
                className="form-control"
                value={formData.spouseName ?? ''}
                onChange={(e) => setFormData({ ...formData, spouseName: e.target.value })}
                disabled={sharedContext.isOcrLocked('user_personal_information.spouse_name', formData.spouseName)}
                title={sharedContext.isOcrLocked('user_personal_information.spouse_name', formData.spouseName) ? 'Verified from your document — change it in the Documents Center' : undefined}
              />
            </div>
            <div className="form-group">
              <label>Spouse Middle Name</label>
              <input
                className="form-control"
                value={formData.spouseMiddleName ?? ''}
                onChange={(e) => setFormData({ ...formData, spouseMiddleName: e.target.value })}
                disabled={sharedContext.isOcrLocked('user_personal_information.spouse_middle_name', formData.spouseMiddleName)}
                title={sharedContext.isOcrLocked('user_personal_information.spouse_middle_name', formData.spouseMiddleName) ? 'Verified from your document — change it in the Documents Center' : undefined}
              />
            </div>
            <div className="form-group">
              <label>Spouse Last Name</label>
              <input
                className="form-control"
                value={formData.spouseLastName ?? ''}
                onChange={(e) => setFormData({ ...formData, spouseLastName: e.target.value })}
                disabled={sharedContext.isOcrLocked('user_personal_information.spouse_last_name', formData.spouseLastName)}
                title={sharedContext.isOcrLocked('user_personal_information.spouse_last_name', formData.spouseLastName) ? 'Verified from your document — change it in the Documents Center' : undefined}
              />
            </div>
            <div className="form-group form-group--full">
              <label>Spouse Maiden Name</label>
              <input
                className="form-control"
                value={formData.spouseMaidenName ?? ''}
                onChange={(e) => setFormData({ ...formData, spouseMaidenName: e.target.value })}
              />
            </div>
          </>
        )}

      </div>

      {!sharedContext.lockedAfterSubmit && (
        <button type="submit" className="btn-save mt-3" disabled={saving || !canEdit}>
        {saving ? 'Submitting...' : 'Submit'}
        </button>
      )}
      {!canEdit && agentReadOnlyNote(delegationPerms, 'KYC') && (
        <small className="text-warning d-block mt-2">
          {agentReadOnlyNote(delegationPerms, 'KYC')}
        </small>
      )}
    </form>
  );
};
